import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { KEYBOARD_SHORTCUTS } from '@app-core/constants/keyboard-shortcuts.constants';
import { DataService } from '@app-core/services/data/data.service';
import { DateService } from '@app-core/services/date/date.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { KeyboardShortcutsService } from '@app-core/services/keyboard-shortcuts/keyboard-shortcuts.service';
import { UpdateDriverIdInDriverQuery, UpdateDriverQuery } from '@app-driver-management/actions/driver-query.actions';
import { DriverQueryState, getDriverQueryState } from '@app-driver-management/reducers';
import { State } from '@app-driver-management/reducers/driver-query.reducer';
import { CLIENT_CONFIG } from '@config/config';
import { Location } from '@angular/common';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { skip, take, takeUntil } from 'rxjs/operators';
import { DriverManagementService } from '../../services/driver-management.service';
import { DriverDetails } from '@app-driver-management/common/driver-management.model';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { NO_DATA_IMAGE } from '@app-driver-management/common/driver-management.constants';
import { MILES_TO_KILOMETERS_CONVERSION } from '@app-core/constants/constants';

const tabNameIndexMapping = {
  overview: 0,
  coaching: 1,
  recognition: 2,
};

@Component({
  selector: 'app-driver-details',
  templateUrl: './driver-details.component.html',
  styleUrls: ['./driver-details.component.scss'],
})
export class DriverDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('paginator')
  public paginator: MatPaginator;
  public showViolationCharts: boolean = true;
  public areStreaksInsufficient: boolean;
  public hideStreakResponeRate: boolean = true;
  public hideDriverResponseRate: boolean = true;
  public hideDrivingAppreciated: boolean = true;
  public driverAppreciatedClass: string = 'd-hide';

  public driverQueryState: DriverQueryState = {} as DriverQueryState;
  public fleetId: string;
  public startDate: string;
  public endDate: string;
  public driverId: string;
  public driverName: string;
  public currentTabIndex: number = 0;
  public clientConfig = CLIENT_CONFIG;
  public keyboardShortcuts = KEYBOARD_SHORTCUTS;
  public currentTheme: string = 'light';
  public currentOS: string = 'windows';
  public currentDateFormat: string;
  public currentMetricUnit: string;
  public driverDetails: DriverDetails;
  public iscoachingRequired: boolean = false;
  public initialStreakResponeRateLoader: boolean = true;
  public initialDrivingAppreciatedLoader: boolean = true;

  public driverEventLoader: boolean = true;
  public driverDetailsLoader: boolean = false;
  public driverListLoader: boolean;

  public noDataImage: string = NO_DATA_IMAGE;

  public driverType: string = null;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private driverManagementService: DriverManagementService,
    private store: Store<State>,
    private gtmService: GoogleTagManagerService,
    public dataService: DataService,
    private dateService: DateService,
    private route: ActivatedRoute,
    private router: Router,
    public translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private location: Location,
    private keyboardShortcutsService: KeyboardShortcutsService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit(): void {
    this.store
      .select(getDriverQueryState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((driverQueryState) => {
        this.driverQueryState = driverQueryState;
        this.startDate = driverQueryState.paramStartDate;
        this.endDate = driverQueryState.paramEndDate;
        this.driverId = driverQueryState.driverId;
      });

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe), take(1)).subscribe((value) => {
      this.fleetId = value;
    });

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe), skip(1)).subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
      }
      this.store.dispatch(new UpdateDriverIdInDriverQuery(''));
    });

    this.dataService._currentTheme.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentTheme = value;
      }
    });

    this.dataService._currentDateFormat.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentDateFormat = value;
      }
    });

    this.dataService._currentMetricUnit.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentMetricUnit = value;
      }
    });

    this.route.queryParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((param: { driverId: string; driverType: string; driverName?: string }) => {
        if (param.driverId) {
          this.store.dispatch(new UpdateDriverIdInDriverQuery(param.driverId));
        } else if (this.driverQueryState.driverId) {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              driverId: this.driverQueryState.driverId,
            },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }

        if (param?.driverType) {
          this.driverType = param.driverType;
        }
        if (param?.driverName?.length > 0) {
          this.driverName = param.driverName;
        }
      });

    this.driverManagementService.driverStats.pipe(takeUntil(this.ngUnsubscribe)).subscribe((driverStats) => {
      this.driverDetails = driverStats;
    });

    this.currentOS = this.dataService.getCurrentOS();
    this.configureKeyboardShortcuts();
  }

  public ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  /**
   * Function called on change of driver query filters.
   *
   * Update local variables being passed as inputs to driver query results cmponent.
   *
   * @param {DriverQueryState} driverQueryOptions
   */
  public onDriverQuery(driverQueryOptions: DriverQueryState): void {
    const { driverId: oldDriverId = '', paramStartDate: oldStartDate = '', paramEndDate: oldEndDate = '' } = this.driverQueryState;
    const {
      paramStartDate: newStartDate = '',
      paramEndDate: newEndDate = '',
      displayStartDate = null,
      displayEndDate = null,
    } = driverQueryOptions || {};
    if (displayStartDate && displayEndDate) {
      const isTimeRangeChanged = newStartDate !== oldStartDate || newEndDate !== oldEndDate;
      if (isTimeRangeChanged) {
        if (this.hideStreakResponeRate && !this.initialStreakResponeRateLoader) {
          this.initialStreakResponeRateLoader = true;
        }
        const noOfDays = this.dateService.getNoOfDays(newStartDate, newEndDate);
        const durationInText = this.dateService.getDurationText(displayStartDate, displayEndDate, noOfDays);
        this.gtmService.changeDriverOverviewDurationFilter(durationInText, noOfDays);
      }
    }

    const DriverDetailsFilter = {
      driverId: oldDriverId,
      ...driverQueryOptions,
    };

    this.startDate = driverQueryOptions.paramStartDate;
    this.endDate = driverQueryOptions.paramEndDate;
    this.driverId = driverQueryOptions.driverId;

    this.store.dispatch(new UpdateDriverQuery(DriverDetailsFilter));
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        driverId: driverQueryOptions.driverId,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  public onPageTabChange(event) {
    this.currentTabIndex = event.index;
    this.cdr.detectChanges();
    const tabName = Object.keys(tabNameIndexMapping).find((key) => tabNameIndexMapping[key] === this.currentTabIndex);
    let displayTabName = '';
    if (tabName === 'overview') {
      displayTabName = 'Clicked overview tab in Driver Details Page - Drivers';
    } else if (tabName === 'coaching') {
      displayTabName = 'Clicked coaching in Driver Details Page - Drivers';
    } else {
      displayTabName = 'Clicked recognition in Driver Details Page - Drivers';
    }
    this.mixpanelService.track(displayTabName, {
      'Tab Name': tabName,
    });
  }

  public configureKeyboardShortcuts() {
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab0[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(0);
      });
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab1[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(1);
      });
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab2[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(2);
      });
  }

  public onKeyboardTabChange(tabIndex: number) {
    const currentTab = Object.entries(tabNameIndexMapping).find(([, idx]) => idx === tabIndex);
    this.currentTabIndex = currentTab[1];
    const tabName = Object.keys(tabNameIndexMapping).find((key) => tabNameIndexMapping[key] === this.currentTabIndex);
    let displayTabName = '';
    if (tabName === 'overview') {
      displayTabName = 'Clicked overview tab in Driver Details Page - Drivers';
    } else if (tabName === 'coaching') {
      displayTabName = 'Clicked coaching in Driver Details Page - Drivers';
    } else {
      displayTabName = 'Clicked recognition in Driver Details Page - Drivers';
    }
    this.mixpanelService.track(displayTabName, {
      'Tab Name': tabName,
    });
  }

  public navigateBack() {
    this.location.back();
  }

  public navigateToCoachingSession() {
    this.gtmService.gotoCoachingSessionFromDriversPage();
    this.router.navigate(['drivers/coaching-session'], {
      queryParams: {
        driverId: this.driverDetails?.driverId || this.driverQueryState.driverId || '',
      },
    });
    this.mixpanelService.track('Navigating to Coaching Session Page', {
      driverId: this.driverDetails?.driverId || '',
    });
  }

  public coachingRequired(coachReq) {
    this.iscoachingRequired = coachReq;
  }

  public noStreak(event: boolean) {
    this.initialStreakResponeRateLoader = false;
    this.areStreaksInsufficient = event;
  }

  public hideResponseRate(event: boolean) {
    this.initialStreakResponeRateLoader = false;
    this.hideDriverResponseRate = event;
    this.cdr.detectChanges();
  }

  public hideAppreciatedEvents(event: boolean): void {
    this.initialDrivingAppreciatedLoader = false;
    this.driverAppreciatedClass = event ? 'd-none' : 'd-flex';
    this.hideDrivingAppreciated = event;
  }

  public getStatsValue(data) {
    this.initialStreakResponeRateLoader = false;
    const distanceInMiles = this.currentMetricUnit === 'Miles' ? data.distance / MILES_TO_KILOMETERS_CONVERSION : data.distance;
    if (distanceInMiles < 100) {
      this.hideStreakResponeRate = true;
    } else {
      this.hideStreakResponeRate = false;
    }
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
